import classNames from 'clsx';
import { isEmpty } from 'lodash';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import StarsButton from '@swf/components/StarsButton/StarsButton';
import Translation from '@swf/components/Translation/Translation';
import { useWidget } from '@swf/core/hooks/useWidget';
import type { Dispatch, RootState } from '@swf/core/store';
import { TileType, UserClubRole } from '@swf/core/types/Poker';
import type { TrackedTranslation } from '@swf/core/types/i18n';
import {
  GameTileSection,
  Loader,
  NoData,
  useCheckUserClubRole,
  useFocusGamesList,
  useHomeGamesWidget,
  useMapGameData
} from '@swf/poker';

import NoDataIcon from '../../Tournaments/assets/no-data.svg';
import type { Props } from '../Container';
import { options as createGameOptions } from '../Header/CreateGameButtons/CreateGameButtons';

import {
  TXTCLI_Cash_Games,
  TXTCLI_Recent_Club_Tournaments,
  TXTCLI_Registered_In_Tournaments,
  TXTCLI_Scheduled_Tournaments
} from './Lobby.messages.json';
import styles from './Lobby.scss';

const Lobby: React.FC<Props> = (props) => {
  const { widgetId } = useWidget();
  const { clubId } = useParams<{ clubId: string }>();
  const { modal } = useDispatch<Dispatch>();
  const isAdminOrManager = useCheckUserClubRole(`${clubId}`, [
    UserClubRole.Admin,
    UserClubRole.Manager
  ]);
  const { widgetSelect, widgetDispatch } = useHomeGamesWidget();

  const { mappedTables, mappedTournaments, mappedRecentTournaments } = useMapGameData({
    shouldMapTables: true,
    shouldMapTournaments: true
  });
  const scheduledTournamentTiles = useSelector((rootState: RootState) =>
    widgetSelect.getScheduledTournTiles(rootState, mappedTournaments)
  );
  const registeredTournamentTiles = useSelector((rootState: RootState) =>
    widgetSelect.getRegisteredTournTiles(rootState, mappedTournaments)
  );

  const showRegistered = !isEmpty(registeredTournamentTiles[clubId]);
  const showScheduled = !isEmpty(scheduledTournamentTiles[clubId]);
  const showTables = !isEmpty(mappedTables[clubId]);
  const showUnavailable = !isEmpty(mappedRecentTournaments[clubId]);

  const handleCreateClick = (path: string) => {
    modal.enqueue({ path, locationState: { clubId: Number(clubId), widgetId } });
  };
  useFocusGamesList(Number(clubId));

  useEffect(() => {
    widgetDispatch.fetchRegisteredTournaments({
      clubId: Number(clubId)
    });
  }, [widgetDispatch, clubId]);

  return (
    <div
      className={classNames(styles.container, {
        [styles.loading]: props.loading
      })}
    >
      <Loader loading={props.loading}>
        {showRegistered && (
          <GameTileSection
            title={<Translation {...TXTCLI_Registered_In_Tournaments} />}
            games={registeredTournamentTiles[clubId]}
            type={TileType.Registered}
          />
        )}
        {showScheduled && (
          <GameTileSection
            title={<Translation {...TXTCLI_Scheduled_Tournaments} />}
            games={scheduledTournamentTiles[clubId]}
            type={TileType.Upcoming}
          />
        )}
        {showTables && (
          <GameTileSection
            title={<Translation {...TXTCLI_Cash_Games} />}
            games={mappedTables[clubId]}
            type={TileType.Cash}
          />
        )}
        {showUnavailable && (
          <GameTileSection
            games={mappedRecentTournaments[clubId]}
            title={<Translation {...TXTCLI_Recent_Club_Tournaments} />}
            type={TileType.Recent}
          />
        )}
        {!showRegistered && !showScheduled && !showTables && !showUnavailable && (
          <NoData
            className={styles.noData}
            icon={<NoDataIcon />}
            // TODO: Translations - status: submitted
            primaryText="No Games to Show"
            secondaryText="The games will be shown here after a tournament or a cash game will be created."
          >
            {isAdminOrManager && (
              <div className={styles.noDataButtons}>
                {createGameOptions.map(({ data, value }) => (
                  <StarsButton
                    key={`${value}`}
                    onClick={() => handleCreateClick(`${value}`)}
                    translation={data as TrackedTranslation}
                  />
                ))}
              </div>
            )}
          </NoData>
        )}
      </Loader>
    </div>
  );
};

export default Lobby;
